import { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Box,
  Card,
  CardContent,
  Stack,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CustomMarkdownEditor from "./CustomMarkdownEditor";
import Divider from "@mui/material/Divider";

import "react-quill/dist/quill.snow.css"; // Quill styles
import "quill-emoji/dist/quill-emoji.css"; // Emoji styles
import PlaceholderInput from "./PlaceholderInput";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";
import { Search } from "@mui/icons-material";
import { useAuth } from "../../hooks/AuthProvider";
import {
  TEMPLATE_APP_CATALOGS,
  TEMPLATE_CATALOG_PRODUCTS,
} from "../../config/urls";

const SingleProductTemplateEditor = ({
  body,
  setBody,
  product,
  setProduct,
  sampleBodyFields,
  setsampleBodyFields,
  footer,
  setFooter,
  bodyDuplicatePlaceholderError,
  setBodyDuplicatePlaceholderError,
}) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [catalogOptions, setCatalogOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const latestProductsRequest = useRef(0);

  useEffect(() => {
    fetchAppCatalogs();
  }, []);

  useEffect(() => {
    if (selectedCatalog?.id) setProductsLoading(true);

    const timer = setTimeout(() => {
      fetchCatalogProducts();
    }, 700);

    return () => clearTimeout(timer);
  }, [selectedCatalog, productSearch]);

  const fetchAppCatalogs = async () => {
    const token = await getValidToken();

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(TEMPLATE_APP_CATALOGS, { headers });

      if (!res.ok) throw new Error("Failed to fetch catalogs");

      const data = await res.json();
      setCatalogOptions(data);

      if (data.length > 0) {
        setSelectedCatalog(data[0]);
      }
    } catch (error) {
      console.error("Error fetching catalogs:", error);
    }
  };

  const fetchCatalogProducts = async () => {
    if (!selectedCatalog?.id) return;

    // Searches can overlap; only the latest request may update the list
    const requestId = ++latestProductsRequest.current;
    setProductsLoading(true);

    try {
      const token = await getValidToken();

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const query = productSearch.trim()
        ? `?search=${encodeURIComponent(productSearch.trim())}`
        : "";

      const res = await fetch(
        `${TEMPLATE_CATALOG_PRODUCTS}${selectedCatalog?.id}${query}`,
        { headers },
      );

      if (!res.ok) throw new Error("Failed to fetch products");

      const data = await res.json();
      if (requestId === latestProductsRequest.current) {
        setProductOptions(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      if (requestId === latestProductsRequest.current) {
        setProductsLoading(false);
      }
    }
  };

  const detectPlaceholders = (text) => {
    const matches = text.match(/\{\{\d+\}\}/g) || [];
    const seen = new Set();
    let hasDuplicate = false;

    matches.forEach((match) => {
      if (seen.has(match)) {
        hasDuplicate = true;
      } else {
        seen.add(match);
      }
    });

    const uniquePlaceholders = matches.filter(
      (match, idx) => matches.indexOf(match) === idx,
    );
    return { hasDuplicate, uniquePlaceholders };
  };

  const handleFooterChange = (event) => {
    const newFooter = event.target.value;

    if (newFooter === "" || newFooter.trim().length > 0) {
      if (newFooter.length <= 60) {
        setFooter(newFooter);
      }
    }
  };

  return (
    <Stack spacing={4}>
      {/* Body */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Grid item xs={12}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  marginTop: 2,
                  marginBottom: 2,
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  Message body *
                </Typography>

                <CustomMarkdownEditor
                  id="template-body"
                  value={body}
                  onChange={(newValue) => {
                    setBody(newValue);
                    const { hasDuplicate, uniquePlaceholders } =
                      detectPlaceholders(newValue);
                    setBodyDuplicatePlaceholderError(hasDuplicate);

                    if (!hasDuplicate) {
                      const newSamples = uniquePlaceholders.map((_, idx) =>
                        sampleBodyFields[idx] !== undefined
                          ? sampleBodyFields[idx]
                          : "",
                      );
                      setsampleBodyFields(newSamples);
                    }
                  }}
                  maxLength={1024}
                />

                {bodyDuplicatePlaceholderError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Duplicate variable placeholders are not allowed.
                  </Typography>
                )}
              </div>
            </Grid>
            {!bodyDuplicatePlaceholderError && sampleBodyFields.length > 0 && (
              <Grid item xs={11}>
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Body Variables
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    width: "100%",
                    gap: 4,
                  }}
                >
                  {sampleBodyFields.map((sample, index) => (
                    <Box
                      key={index}
                      sx={{
                        marginBottom: "16px",
                        display: "flex",
                        flex: { xs: 1, md: 0.5 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ display: "inline-block", mr: 1 }}
                      >
                        {"{{" + (index + 1) + "}}:"}
                      </Typography>
                      <PlaceholderInput
                        sample={sample}
                        sampleBodyFields={sampleBodyFields}
                        index={index}
                        setsampleBodyFields={setsampleBodyFields}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            <Divider />

            {/* Footer */}
            <Grid item xs={12}>
              <Typography variant="h6">
                Footer <span style={{ opacity: 0.6 }}>(Optional)</span>
              </Typography>
              <TextField
                sx={{
                  width: "100%",
                  marginTop: 2,
                  marginBottom: 2,
                }}
                label="You can use this space to add a tagline, a way to unsubscribe, etc."
                value={footer}
                onChange={handleFooterChange}
                inputProps={{
                  maxLength: 60,
                  style: { paddingRight: "40px" },
                }}
                InputProps={{
                  endAdornment: (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "gray",
                        position: "absolute",
                        right: "10px",
                        top: "12px",
                      }}
                    >
                      {`${footer.length}/60`}
                    </Typography>
                  ),
                }}
              />
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* Product */}
      <Card>
        <CardContent>
          <Stack gap={3} sx={{ width: "100%" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Product{" "}
                <Typography component="span" sx={{ color: ColorsConstants.error }}>
                  *
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ color: grayFontColor, mt: 0.5 }}>
                Select a catalogue, then pick the single product this
                template will showcase.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={catalogOptions || []}
                  value={selectedCatalog}
                  onChange={(e, value) => {
                    setSelectedCatalog(value);
                    setProductSearch("");
                    setProduct(null);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  getOptionLabel={(option) => option?.name || ""}
                  renderOption={(props, option) => (
                    <Stack
                      {...props}
                      key={option?.id}
                      direction="column"
                      gap={0.5}
                      sx={{
                        alignItems: "flex-start !important",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <Typography>{option?.name}</Typography>
                      <Typography
                        variant="body2"
                        color={ColorsConstants.primaryGray40}
                      >
                        Catalog ID: {option?.id}
                      </Typography>
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Catalog" fullWidth required />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 14,
                    },
                  }}
                  noOptionsText="No catalogs found"
                />
                <Typography variant="caption" sx={{ color: grayFontColor }}>
                  Auto-detected from your connected WhatsApp Business Account.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  disabled={!selectedCatalog}
                  options={productOptions || []}
                  value={product}
                  onChange={(e, value) => {
                    setProduct(value);
                    setProductSearch("");
                  }}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === "input") {
                      setProductSearch(newValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.retailer_id === value?.retailer_id
                  }
                  filterOptions={(options) => options}
                  loading={productsLoading}
                  loadingText="Searching products..."
                  getOptionLabel={(option) => option?.name || ""}
                  renderOption={(props, option) => (
                    <Stack
                      {...props}
                      key={option?.retailer_id}
                      direction="column"
                      gap={0.5}
                      sx={{
                        alignItems: "flex-start !important",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <Typography>{option?.name}</Typography>
                      <Typography
                        variant="body2"
                        color={ColorsConstants.primaryGray40}
                      >
                        Retailer ID: {option?.retailer_id}
                      </Typography>
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product"
                      placeholder="Search by product name"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Search
                            sx={{ color: ColorsConstants.primaryGray40, mr: 1 }}
                          />
                        ),
                        endAdornment: (
                          <>
                            {productsLoading && (
                              <CircularProgress color="inherit" size={18} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 14,
                    },
                  }}
                  noOptionsText="No products found"
                />
                <Typography variant="caption" sx={{ color: grayFontColor }}>
                  Shown as the product in the message.
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};
export default SingleProductTemplateEditor;
